import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiBox,
  FiCalendar,
  FiCheckCircle,
  FiAlertTriangle,
  FiX
} from "react-icons/fi";

const ReceivePO = () => {
  const { poNumber } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState([]);
  const [outlet, setOutlet] = useState({ name: "" });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isFullyReceived, setIsFullyReceived] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!poNumber) return;
      const token = localStorage.getItem("token");
      if (!token) return alert("Token tidak ditemukan");

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/purchasing-order/${encodeURIComponent(poNumber)}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        const result = await res.json();
        const poItems = result.data.items.map(item => {
          const totalReceived = item.logs.reduce((acc, log) => acc + Number(log.qty), 0);
          const remaining = Math.max(item.qty - totalReceived, 0);
          return {
            id: item.obat?.id,
            name: item.obat?.name,
            qtyOrder: item.qty,
            logs: item.logs,
            totalReceived,
            remainingQty: remaining,
            qtyReceive: remaining,
            expiredAt: ""
          };
        });

        setFormData(poItems);
        setOutlet(result.data.outlet || { name: "Outlet" });
      } catch (err) {
        console.error(err);
        alert("Gagal mengambil data PO");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [poNumber]);

  useEffect(() => {
    const isAllReceived = formData.every(item => Number(item.qtyReceive) >= item.remainingQty);
    setIsFullyReceived(isAllReceived);
  }, [formData]);

  const handleChange = (index, field, value) => {
    const updated = [...formData];
    if (field === "qtyReceive") {
      const max = updated[index].remainingQty;
      const safeVal = Math.min(Number(value), max);
      updated[index][field] = safeVal;
    } else {
      updated[index][field] = value;
    }
    setFormData(updated);
  };

  const handleSubmit = () => setShowModal(true);

  const confirmSubmit = async () => {
    const token = localStorage.getItem("token");
    const payload = {
      received: isFullyReceived,
      items: formData.map(item => ({
        id: item.id,
        qty: Number(item.qtyReceive),
        expired_at: item.expiredAt
      }))
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/purchasing-order/${encodeURIComponent(poNumber)}/receive`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        }
      );
      if (!res.ok) throw new Error("Gagal menyimpan data");

      setShowModal(false);
      setShowSuccess(true);
    } catch (err) {
      alert(err.message);
    }
  };

  const closeSuccess = () => {
    setShowSuccess(false);
    navigate(-1);
    setTimeout(() => {
      window.location.reload();
    }, 300); // beri delay 300ms agar navigasi sempat terjadi
  };  

  if (loading) return <p>Loading...</p>;

  return (
    <div className="p-6 bg-white rounded-xl">
      <h1 className="text-2xl font-bold mb-2 flex items-center gap-2 text-gray-800">
        <FiBox className="text-green-600" />
        Receive {poNumber}
      </h1>
      <p className="text-gray-600 mb-4">
        Silakan isi jumlah barang yang diterima di outlet <b>{outlet.name}</b>
      </p>

      {/* Table */}
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 border">No</th>
              <th className="px-4 py-3 border">Name & Logs</th>
              <th className="px-4 py-3 border">Qty Order</th>
              <th className="px-4 py-3 border">Qty Received</th>
              <th className="px-4 py-3 border">Qty Input</th>
              <th className="px-4 py-3 border">Expired At</th>
            </tr>
          </thead>
          <tbody>
            {formData.map((item, idx) => (
              <tr key={item.id} className="border-t">
                <td className="px-4 py-2 border">{idx + 1}</td>
                <td className="px-4 py-2 border">
                  <div className="font-semibold text-gray-800">{item.name}</div>
                  {item.logs.length > 0 &&
                    item.logs.map((log, li) => (
                      <div
                        key={li}
                        className="text-xs text-gray-600 flex items-center gap-1"
                      >
                        <FiCheckCircle className="text-green-500" />
                        <span>
                          Received —{" "}
                          <strong>
                            {new Date(log.log_at).toLocaleString()}
                          </strong>{" "}
                          — qty: {log.qty}
                        </span>
                      </div>
                    ))}
                </td>
                <td className="px-4 py-2 border text-center">{item.qtyOrder}</td>
                <td className="px-4 py-2 border text-center">{item.totalReceived}</td>
                <td className="px-4 py-2 border">
                  <input
                    type="number"
                    min={0}
                    max={item.remainingQty}
                    value={item.qtyReceive}
                    className="w-full px-2 py-1 border rounded focus:ring-green-400"
                    onChange={e =>
                      handleChange(idx, "qtyReceive", e.target.value)
                    }
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Max: {item.remainingQty}
                  </p>
                </td>
                <td className="px-4 py-2 border">
                  <input
                    type="date"
                    value={item.expiredAt}
                    className="w-full px-2 py-1 border rounded focus:ring-green-400"
                    onChange={e =>
                      handleChange(idx, "expiredAt", e.target.value)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Checkbox */}
      <div className="flex items-center mt-4 gap-2">
        <input
          type="checkbox"
          id="isReceived"
          checked={isFullyReceived}
          onChange={() => setIsFullyReceived(!isFullyReceived)}
          className="w-5 h-5 text-green-600 border-gray-300 rounded"
        />
        <label htmlFor="isReceived" className="text-gray-800">
          Semua barang sudah diterima (stok lengkap)
        </label>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        className="mt-6 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold flex items-center gap-2"
      >
        <FiCheckCircle /> Submit
      </button>

{/* Confirmation Modal */}
{showModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 px-4">
    <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <FiAlertTriangle className="text-red-600 text-2xl" />
        <h2 className="text-xl font-semibold text-red-600">Konfirmasi</h2>
      </div>

      {/* Info */}
      <p className="text-gray-700 mb-4 text-sm">
        {formData.length} item obat akan ditambahkan ke outlet{" "}
        <span className="font-bold text-red-600">{outlet.name}</span>
      </p>

      {/* Table Wrapper (scroll kalau lebih dari 5 row) */}
      <div className="overflow-x-auto rounded-xl border shadow-sm mb-4">
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead className="bg-gray-100 text-gray-700 sticky top-0">
              <tr>
                <th className="border px-4 py-2">No</th>
                <th className="border px-4 py-2">Name</th>
                <th className="border px-4 py-2">Qty Receive</th>
                <th className="border px-4 py-2">Expired At</th>
              </tr>
            </thead>
            <tbody>
              {formData.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50 text-gray-800">
                  <td className="border px-4 py-2 text-center">{idx + 1}</td>
                  <td className="border px-4 py-2">{item.name}</td>
                  <td className="border px-4 py-2 text-center">{item.qtyReceive}</td>
                  <td className="border px-4 py-2 text-center">{item.expiredAt || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex justify-end gap-3 mt-auto">
        <button
          className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg"
          onClick={() => setShowModal(false)}
        >
          <FiX className="text-lg" /> Cancel
        </button>
        <button
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow"
          onClick={confirmSubmit}
        >
          <FiCheckCircle className="text-lg" /> Confirm
        </button>
      </div>
    </div>
  </div>
)}


      {/* Modal Success */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold text-green-600 flex items-center gap-2 mb-4">
              <FiCheckCircle /> Berhasil
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              Purchase Order <b>{poNumber}</b> berhasil diterima.
            </p>
            <div className="flex justify-end">
              <button
                onClick={closeSuccess}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivePO;
